import { useEffect, useState } from 'react';
import { Button, Card, ErrorNotice, Icon } from '@hive/ui';
import { api, errorMessage, type VenueSetup } from '../api/client';

/**
 * Venue sound and sync: one live offset for every screen (so screens on the same stream
 * play together) and one screen that carries the sound.
 */
export function SyncCard({ setup, onSaved }: { setup: VenueSetup; onSaved(next: VenueSetup): void }) {
  const [target, setTarget] = useState('');
  const [audio, setAudio] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setTarget(setup.venue.syncTargetSeconds ? String(setup.venue.syncTargetSeconds) : '');
    setAudio(setup.venue.audioScreenId ?? '');
  }, [setup]);

  async function save() {
    const seconds = target.trim() ? Number(target) : 0;
    if (!Number.isFinite(seconds) || seconds < 0 || seconds > 60) return setError('Use a number of seconds from 0 to 60, or leave it empty.');
    setBusy(true);
    setError(null);
    setSaved(false);
    const { data, error: err } = await api.PATCH('/v1/setup/venues/{venueId}', {
      params: { path: { venueId: setup.venue.id } },
      body: { syncTargetSeconds: seconds, audioScreenId: audio },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(err));
    setSaved(true);
    onSaved(data);
  }

  return (
    <Card padded className="mc-side-card">
      <span className="mc-section-title">
        <Icon name="sound" size={16} />
        &nbsp;Sound and screens together
      </span>
      {error && <ErrorNotice title={error} />}
      <div className="mc-grid2">
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="sync-target">
            Play this far behind live (seconds)
          </label>
          <input id="sync-target" className="hv-input" inputMode="decimal" placeholder="Each stream's default" value={target} onChange={(e) => setTarget(e.target.value)} />
          <span className="mc-tiny hv-muted">Every screen holds the same offset, nudging its speed by up to 3%, so screens on one stream stay together.</span>
        </div>
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="sync-audio">
            Screen that carries the sound
          </label>
          <select id="sync-audio" className="hv-input" value={audio} onChange={(e) => setAudio(e.target.value)}>
            <option value="">Every screen plays sound</option>
            {setup.screens.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <span className="mc-tiny hv-muted">The others are muted, so the room hears one source instead of an echo.</span>
        </div>
      </div>
      <div className="mc-row-between">
        <span className="mc-small hv-muted">{saved ? 'Saved. Players pick it up straight away.' : 'How close screens get is measured on the live venue page.'}</span>
        <Button onClick={save} disabled={busy}>
          Save
        </Button>
      </div>
    </Card>
  );
}
